import { useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { toast } from '@/components/ui/Toast'
import { useAuth } from '@/hooks/useAuth'

const C = { teal: '#0F5C5C', border: '#E1E5E3', ink: '#202523', ink2: '#6D7471' }
const FB = "'Inter', sans-serif"
const FH = "'Plus Jakarta Sans', sans-serif"

const fieldStyle: React.CSSProperties = { marginBottom: 14 }
const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 700,
  color: C.ink2,
  marginBottom: 6,
}
const inputStyle: React.CSSProperties = {
  width: '100%',
  border: `1px solid ${C.border}`,
  borderRadius: 11,
  padding: '12px 14px',
  fontFamily: FB,
  fontSize: 14,
  color: C.ink,
  outline: 'none',
}

interface Props {
  onSuccess: (needsEmailConfirmation: boolean) => void
}

export default function RegisterClientForm({ onSuccess }: Props) {
  const { signUp } = useAuth()
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPass, setShowPass] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (password.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres')
      return
    }
    if (password !== confirmPassword) {
      toast.error('Las contraseñas no coinciden')
      return
    }
    setLoading(true)
    try {
      const { needsEmailConfirmation } = await signUp(email, password, username)
      onSuccess(needsEmailConfirmation)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo crear la cuenta')
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <div style={fieldStyle}>
        <label style={labelStyle}>Nombre de usuario</label>
        <input
          style={inputStyle}
          type="text"
          placeholder="tu_usuario"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          minLength={2}
          maxLength={30}
          required
          onFocus={(e) => (e.target.style.borderColor = C.teal)}
          onBlur={(e) => (e.target.style.borderColor = C.border)}
        />
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Correo electrónico</label>
        <input
          style={inputStyle}
          type="email"
          placeholder="tu@correo.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
          onFocus={(e) => (e.target.style.borderColor = C.teal)}
          onBlur={(e) => (e.target.style.borderColor = C.border)}
        />
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Contraseña</label>
        <div style={{ position: 'relative' }}>
          <input
            style={{ ...inputStyle, paddingRight: 44 }}
            type={showPass ? 'text' : 'password'}
            placeholder="Mínimo 8 caracteres"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={8}
            required
            autoComplete="new-password"
            onFocus={(e) => (e.target.style.borderColor = C.teal)}
            onBlur={(e) => (e.target.style.borderColor = C.border)}
          />
          <button
            type="button"
            onClick={() => setShowPass((v) => !v)}
            style={{
              position: 'absolute',
              right: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
            }}
          >
            {showPass ? <EyeOff size={17} color={C.ink2} /> : <Eye size={17} color={C.ink2} />}
          </button>
        </div>
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Confirmar contraseña</label>
        <input
          style={inputStyle}
          type={showPass ? 'text' : 'password'}
          placeholder="Repite tu contraseña"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          autoComplete="new-password"
          onFocus={(e) => (e.target.style.borderColor = C.teal)}
          onBlur={(e) => (e.target.style.borderColor = C.border)}
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        style={{
          width: '100%',
          padding: 14,
          borderRadius: 12,
          border: 'none',
          background: C.teal,
          color: '#fff',
          fontFamily: FH,
          fontWeight: 700,
          fontSize: 15,
          cursor: loading ? 'not-allowed' : 'pointer',
          opacity: loading ? 0.7 : 1,
        }}
      >
        {loading ? 'Creando cuenta…' : 'Crear cuenta'}
      </button>
    </form>
  )
}
